import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Container,
  Paper,
  Stack,
  Link,
  FormLabel
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { removeItem } from '../../utils/local-storage';
import { useLoginMutation } from './services/authenticationApi';
import { globalVal } from '../../utils/global-values';
import { validateLoginField } from './loginValidation';
import CircularProgress from '@mui/material/CircularProgress';
import { enqueueSnackbar } from 'notistack';

interface FormData {
  userName: string;
  password: string;
}
const LoginPage = () => {
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPasswordReadOnly, setIsPasswordReadOnly] = useState(true);
  const [passwordInputType, setPasswordInputType] = useState<'text' | 'password'>('text');
  const navigate = useNavigate();
  const [login, { isLoading }] = useLoginMutation();
  
  // Rastgele password field name/id oluştur (her render'da aynı kalması için useMemo)
  const randomPasswordId = useMemo(() => 'pwd_' + Math.random().toString(36).substring(2, 15), []);

  // Login sayfasına geldiğimizde global logout durumunu sıfırla
  useEffect(() => {
    globalVal.isLoggedOut = false;
  }, []);

  const onSubmit = async (data: FormData) => {
    setError(false);
    setIsSubmitting(true);
    removeItem('loginTokenModel');
    globalVal.isLoggedOut = false;
    try {
      const response = await login(data).unwrap();
      if (response.success) {
        const decodedToken: any = jwtDecode(response.data.token);
        const tokenRoles = decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
        setIsFadingOut(true);
        setTimeout(() => {
          navigate(tokenRoles[0] === 'ChangePassword' ? '/authentication/change-pass' : '/authentication/verify');
        }, 400); // 400ms fade süresi
      }
    } catch (e: any) {
      setError(true);
      // Hata durumunda da global değişkeni kontrol edelim
      if (e?.status !== 401) {
        globalVal.isLoggedOut = false;
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(onSubmit)();
    }
  };

  return (
    <>
      <Box
        className={isFadingOut ? 'fade-out' : ''}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100vw',
          height: '100vh',
          pb: 1,
          gap: 2
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
            clipPath: {
              xs: 'none',
              md: 'polygon(0 0, 51% 0, 39% 100%, 0% 100%)'
            }
          }}
        />
        <Container
          maxWidth={false}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
            gap: 2
          }}
        >
          <Box
            sx={{
              width: {
                xs: '80%',
                xl: '51.4%'
              },
              maxWidth: '984px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: {
                xs: 'center',
                md: 'flex-start'
              },
              justifyContent: 'center',
              zIndex: 20
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: {
                  xs: '20px',
                  md: '30px',
                  xl: '33px'
                },
                color: '#FFFFFF'
              }}
            >
              AHLATCI
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 'normal',
                marginTop: '8px',
                textAlign: 'center',
                fontSize: {
                  xs: '14px',
                  lg: '20px',
                  '4xl': '27px'
                },
                color: '#FFFFFF'
              }}
            >
              Ödeme ve Elektronik Para Hizmetleri A.Ş.
            </Typography>
          </Box>
          <Paper
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            autoComplete="off"
            elevation={3}
            sx={{
              boxShadow: "5px 6px 20px 0px rgba(30,30,30,.5) !important",
              width: {
                xs: '90%',
                xl: '51.4%'
              },
              maxWidth: '984px',
              padding: {
                xs: 0,
                md: "60px 55px"
              },
              display: 'flex',
              flexDirection: {
                xs: 'column',
                md: 'row'
              },
              alignItems: 'center',
              justifyContent: 'space-between',
              zIndex: 20,
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
              borderRadius: '35px',
              border: '1.2px solid rgba(0, 0, 0, 0.1)',
              gap: {
                xs: 2,
                md: 5
              }
            }}
          >
            <Box
              sx={{
                width: {
                  xs: '40%', // reduced from 60% to 40% for mobile
                  md: '50%'
                },
                p: {
                  xs: 1.5, // reduced padding for mobile
                  md: 2.5
                },
                color: 'white'
              }}
            >
              <img
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundSize: 'cover',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center'
                }}
                src={`${process.env.PUBLIC_URL}/assets/images/logo/newlogo.png`}
                alt="Logo"
              />
            </Box>
            <Stack
              spacing={2}
              sx={{
                width: {
                  xs: '100%',
                  md: '75%'
                },
                backgroundColor: {
                  xs: 'white',
                  md: 'transparent'
                },
                padding: {
                  xs: 2.5,
                  md: 0
                },
                borderBottomLeftRadius: {
                  xs: '35px',
                  md: 0
                },
                borderBottomRightRadius: {
                  xs: '35px',
                  md: 0
                },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  paddingBottom: {
                    xs: '16px !important',
                    md: 0
                  },
                  fontWeight: 800,
                  fontSize: {
                    xs: '16px',
                    md: '22px'
                  },
                  color: 'black'
                }}
              >
                Giriş Yap
              </Typography>
              
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: {
                    xs: '100%',
                    md: '85%'
                  },
                  alignItems: 'flex-start',
                  justifyContent: 'center'
                }}
              >
                <FormLabel
                  htmlFor="emailOrPhone"
                  sx={{
                    marginBottom: '8px',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    color: 'black',
                    '&::before': {
                      content: '"* "',
                      color: 'red'
                    }
                  }}
                >
                  E-posta veya Telefon
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  type="text"
                  id="emailOrPhone"
                  autoComplete="username"
                  placeholder="Email veya telefon numarası giriniz."
                  {...register('userName', {
                    required: 'Geçersiz giriş bilgisi',
                    validate: (value) => {
                      const { valid, message } = validateLoginField(value ?? '');
                      return valid || message;
                    }
                  })}
                  error={!!errors.userName}
                  helperText={errors.userName ? errors.userName.message : ''}
                  onKeyDown={handleKeyDown}
                />
              </Box>
              
              {/* Fake email field - tarayıcının şifre ile gerçek email'i eşleştirmesini engellemek için */}
              <input
                type="text"
                name="fake-email"
                autoComplete="username"
                style={{
                  position: 'absolute',
                  opacity: 0,
                  height: 0,
                  width: 0,
                  pointerEvents: 'none',
                  left: '-9999px'
                }}
                tabIndex={-1}
                aria-hidden="true"
              />
              
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: {
                    xs: '100%',
                    md: '85%'
                  },
                  alignItems: 'flex-start',
                  justifyContent: 'center'
                }}
              >
                <FormLabel
                  htmlFor="password"
                  sx={{
                    marginBottom: '8px',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    color: 'black',
                    '&::before': {
                      content: '"* "',
                      color: 'red'
                    }
                  }}
                >
                  Şifre
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  id={randomPasswordId}
                  type={passwordVisible ? 'text' : passwordInputType}
                  autoComplete="off"
                  {...register('password', { required: 'Lütfen Şifrenizi Giriniz!' })}
                  error={!!errors.password}
                  helperText={errors.password ? errors.password.message : ''}
                  onKeyDown={handleKeyDown}
                  onFocus={() => {
                    setIsPasswordReadOnly(false);
                    setPasswordInputType('password');
                  }}
                  InputProps={{
                    readOnly: isPasswordReadOnly,
                    autoComplete: 'off',
                    endAdornment: (
                      <IconButton onClick={() => setPasswordVisible(!passwordVisible)} edge="end">
                        {passwordVisible ? <VisibilityOff sx={{ color: '#00ADEF' }} /> : <Visibility sx={{ color: '#00ADEF' }} />}
                      </IconButton>
                    ),
                  }}
                  inputProps={{
                    autoComplete: 'off',
                    'data-form-type': 'other',
                    'data-lpignore': 'true',
                    form: randomPasswordId,
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: {
                    xs: '100%',
                    md: '80%'
                  },
                  alignItems: 'flex-end',
                  justifyContent: 'center'
                }}
              >
                <RouterLink
                  to="/authentication/forget-pass"
                  style={{
                    fontWeight: 'normal',
                    fontSize: '13px',
                    borderBottom: '1px solid #00ADEF',
                    color: '#00ADEF',
                    textDecoration: 'none'
                  }}
                >
                  Şifremi unuttum
                </RouterLink>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: {
                    xs: '100%',
                    md: '85%'
                  },
                  alignItems: 'flex-start',
                  justifyContent: 'center'
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={isLoading || isSubmitting}
                  sx={{
                    boxShadow: '0 4px 16px rgba(30, 60, 114, 0.25)',
                    width: '100%',
                    height: {
                      xs: '48px',
                      md: '52px'
                    },
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                    borderColor: '#1e3c72',
                    borderRadius: '12px',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1a3569 0%, #25478a 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 24px rgba(30, 60, 114, 0.35)'
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                      boxShadow: '0 2px 8px rgba(30, 60, 114, 0.25)'
                    },
                    '&:disabled': {
                      background: 'rgba(30, 60, 114, 0.3)',
                      color: 'rgba(255, 255, 255, 0.6)'
                    },
                    color: 'white',
                    fontSize: '15px',
                    fontWeight: 600,
                    textTransform: 'none',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Giriş Yap'}
                </Button>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: {
                    xs: '100%',
                    md: '80%'
                  },
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5
                }}
              >
                <Link
                  href="https://www.ahlpay.com.tr/basvuru"
                  target="_blank"
                  sx={{
                    fontWeight: 'normal',
                    fontSize: {
                      xs: '13px',  // smaller font size for mobile
                      md: '15px'   // original size for desktop
                    },
                    transition: 'all 0.3s',
                    borderBottom: '1px solid #00ADEF',
                    color: '#00ADEF',
                    textDecoration: 'none'
                  }}
                >
                  Kurumsal Hesap Başvuru
                </Link>
              </Box>
            </Stack>
          </Paper>
        </Container>

        {/* Footer */}
        <Box
          sx={{
            zIndex: 20,
            width: '100%',
            px: 2,
            gap: 0.5,
            display: 'flex',
            flexDirection: {
              xs: 'column',
              md: 'row'
            },
            alignItems: 'center',
            justifyContent: {
              xs: 'center',
              md: 'space-between'
            },
          }}
        >
          <Box
            sx={{
              order: {
                xs: 2,
                md: 1
              }
            }}
          >
            <Box
              component="img"
              sx={{
                width: '450px',
                height: '65px',
                display: { xs: 'none', md: 'block' }
              }}
              src={`${process.env.PUBLIC_URL}/assets/images/logo/qrcode2.png`}
              alt="QR Code"
            />
            <Box
              component="img"
              sx={{
                display: { xs: 'block', md: 'none' }
              }}
              src={`${process.env.PUBLIC_URL}/assets/images/logo/qrmobile.png`}
              alt="QR Code Mobile"
            />
          </Box>
          <Stack
            sx={{
              order: {
                xs: 1,
                md: 2
              },
              flexDirection: 'column',
              alignItems: {
                xs: 'center',
                md: 'flex-end'
              },
              justifyContent: 'center'
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: {
                  xs: '11px',
                  md: '18px'
                },
                color: 'black'
              }}
            >
              444 90 66
            </Typography>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: {
                  xs: '11px',
                  md: '18px'
                },
                color: 'black'
              }}
            >
              info@ahlpay.com.tr
            </Typography>
          </Stack>
        </Box>
      </Box>
      <style>{`
        .fade-out {
          animation: fadeOutLogin 0.4s forwards;
        }
        @keyframes fadeOutLogin {
          from { opacity: 1; }
          to { opacity: 0; }
        }
      `}</style>
    </>
  );
};

export default LoginPage;